// MyPostWidget.jsx

import { DeleteOutlined, Close, HelpOutline } from "@mui/icons-material";
import {
  Alert,
  Box,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  TextField,
  Collapse,
  ClickAwayListener,
} from "@mui/material";
import FlexBetween from "components/FlexBetween";
import Dropzone from "react-dropzone";
import WidgetWrapper from "components/WidgetWrapper";
import UserImage from "components/UserImage";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPosts } from "state";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const MAX_BESCHREIBUNG_LENGTH = 280;
const INFO_TOOLTIP_SX = {
  fontSize: "0.8rem",
  px: 1.25,
  py: 0.85,
  maxWidth: 280,
};

const MyPostWidget = ({ onPostCreated, picturePath }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isImage, setIsImage] = useState(false);
  const [images, setImages] = useState([]);
  const [caption, setCaption] = useState("");
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const { palette } = useTheme();
  const isMobile = useMediaQuery("(max-width:600px)");
  const posts = useSelector((state) => state.posts) || [];
  const user = useSelector((state) => state.user);
  const _id = user?._id;
  const token = useSelector((state) => state.token);
  const mediumMain = palette.neutral.mediumMain;
  const medium = palette.neutral.medium;
  const displayPicturePath = picturePath || user?.picturePath || "/assets/misc/DefaultPFP.png";
  const imagePreviewUrlMapRef = useRef(new Map());

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: "8px",
    boxShadow: 24,
    width: "min(520px, calc(100% - 2rem))",
    p: { xs: 2.5, sm: 4 },
  };

  const handlePost = async () => {
    if (isPosting) {
      return;
    }
    if (!_id || !token) {
      setLoginModalOpen(true);
      return;
    }
    if (images.length < 1) {
      return;
    }

    const formData = new FormData();
    formData.append("caption", caption.trim());
    images.forEach((image) => formData.append("images", image));

    setIsPosting(true);

    try {
      const response = await fetch(`${BASE_URL}/posts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Beitrag konnte nicht erstellt werden.");
      }

      dispatch(setPosts({ posts: [data, ...posts] }));
      if (onPostCreated) onPostCreated();
      setImages([]);
      setCaption("");
      setIsImage(false);
    } catch (error) {
      setSnackbarState({
        open: true,
        message: error.message || "Beitrag konnte nicht erstellt werden.",
        severity: "error",
      });
    } finally {
      setIsPosting(false);
    }
  };

  const handleComposerOpen = () => {
    if (!_id) {
      setLoginModalOpen(true);
      return;
    }
    setIsImage(true);
  };

  const handleCaptionChange = (event) => {
    setCaption(event.target.value.slice(0, MAX_BESCHREIBUNG_LENGTH));
  };

  const previewImages = useMemo(() => {
    return images.map((image, index) => {
      if (!imagePreviewUrlMapRef.current.has(image)) {
        imagePreviewUrlMapRef.current.set(image, URL.createObjectURL(image));
      }
      return {
        id: `${image.name}-${index}-${image.lastModified}`,
        name: image.name,
        url: imagePreviewUrlMapRef.current.get(image),
      };
    });
  }, [images]);

  useEffect(() => {
    const activeImages = new Set(images);
    imagePreviewUrlMapRef.current.forEach((url, file) => {
      if (!activeImages.has(file)) {
        URL.revokeObjectURL(url);
        imagePreviewUrlMapRef.current.delete(file);
      }
    });
  }, [images]);

  useEffect(() => {
    const previewMap = imagePreviewUrlMapRef.current;
    return () => {
      previewMap.forEach((url) => URL.revokeObjectURL(url));
      previewMap.clear();
    };
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setIsImage(false)}>
      <WidgetWrapper mb="1.75rem">
      <Box
        display="flex"
        alignItems={isMobile ? "stretch" : "center"}
        justifyContent="space-between"
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 1.25 : 1}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="0.8rem"
          sx={{ minWidth: 0, flex: 1 }}
        >
          {_id && (
            <Box sx={{ alignSelf: "flex-start" }}>
              <UserImage image={displayPicturePath} size="56px" />
            </Box>
          )}
          <Box display="flex" flexDirection="column" sx={{ minWidth: 0, flex: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={isMobile ? "flex-start" : "flex-start"}
              gap={0.45}
              sx={{ minWidth: 0, flex: 1 }}
            >
              {!isImage ? (
                <Button
                  onClick={handleComposerOpen}
                  sx={{
                    color: mediumMain,
                    textTransform: "none",
                    padding: 0,
                    minWidth: "auto",
                    justifyContent: "flex-start",
                    textAlign: "left",
                    fontSize: "1.15rem",
                    fontWeight: 400,
                    lineHeight: 1.5,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                      cursor: "pointer",
                      color: medium,
                    },
                  }}
                >
                  Kaleidoskop hier hochladen
                </Button>
              ) : (
                <TextField
                  placeholder="Beschreibung"
                  fullWidth
                  multiline
                  minRows={1}
                  value={caption}
                  onChange={handleCaptionChange}
                  inputProps={{ maxLength: MAX_BESCHREIBUNG_LENGTH }}
                  variant="standard"
                  sx={{
                    maxWidth: isMobile ? "100%" : "420px",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "1.15rem",
                    lineHeight: 1.5,
                    minHeight: "2.2rem",
                    "& .MuiInputBase-input": {
                      fontSize: "1.15rem",
                      lineHeight: 1.5,
                      padding: 0,
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: mediumMain,
                      opacity: 1,
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          alignItems={isMobile ? "flex-end" : "center"}
          flexDirection={isMobile ? "column" : "row"}
          justifyContent={isMobile ? "flex-end" : "flex-start"}
          gap={0.5}
          flexShrink={0}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Button
              disabled={!isImage || images.length < 1 || !_id || isPosting}
              onClick={handlePost}
              sx={{
                color: "white",
                backgroundColor: palette.primary.main,
                borderRadius: "3rem",
                opacity: isImage && images.length >= 1 && _id && !isPosting ? 1 : 0.7,
                transition: "opacity 0.3s ease",
                "&.Mui-disabled": {
                  color: "white",
                  opacity: 0.7,
                },
                "&:hover": {
                  backgroundColor: palette.primary.dark,
                },
                fontWeight: "bold",
              }}
            >
              {isPosting ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} sx={{ color: "white" }} />
                  <span>POST</span>
                </Box>
              ) : (
                "POST"
              )}
            </Button>
            <IconButton
              size="small"
              onClick={() => setInfoModalOpen(true)}
            >
              <HelpOutline sx={{ fontSize: "20px", color: mediumMain }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      {isImage && (
        <Typography
          mt={0.5}
          ml={!isMobile && _id ? "calc(56px + 0.8rem)" : 0}
          color={caption.length >= MAX_BESCHREIBUNG_LENGTH ? "warning.main" : mediumMain}
          fontSize="0.8rem"
          textAlign={isMobile ? "right" : "left"}
        >
          {caption.length}/{MAX_BESCHREIBUNG_LENGTH} Zeichen
        </Typography>
      )}

      {/* Info Modal */}
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        disableScrollLock
      >
        <DialogTitle sx={{ pr: 7 }}>
          Wettbewerb – Dein eigenes Kunstkaleidoskop hochladen
          <IconButton
            aria-label="close"
            onClick={() => setInfoModalOpen(false)}
            sx={{
              position: "absolute",
              right: 10,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Hier können teilnehmende Schüler bald ihre fertig gestalteten Kunstkaleidoskope als Bilder und Videos hochladen. Mit einem Klick auf den blauen Post-Button wird der Upload-Prozess gestartet. Bitte beachten Sie, dass das Kunstwerk erst dann als offizielles Kunstkaleidoskop gilt, wenn es von uns freigegeben und auf dieser Seite veröffentlicht wurde.
          </Typography>
          <Typography gutterBottom>
            <strong>Für die ersten 100 eingereichten Kunstkaleidoskope</strong> erfolgt der Upload noch per E-Mail, da sich die Seite im Aufbau befindet. Bitte senden Sie die Fotos Ihres gestalteten Rohlings und das Video an: <a href="mailto:info@kaleidoskop-hamburg.de">info@kaleidoskop-hamburg.de</a>.
          </Typography>
          <Typography gutterBottom>
            <strong>Hinweis:</strong> Das Video darf eine Länge von 15 Sekunden nicht überschreiten, da es andernfalls aus rechtlichen Gründen gekürzt werden muss.
          </Typography>
        </DialogContent>
      </Dialog>

      <Collapse in={isImage} timeout={280}>
        <Box
          mt="1.25rem"
          p={0}
        >
          <Dropzone
            acceptedFiles=".jpg,.jpeg,.png,.webp"
            multiple
            maxFiles={2}
            disabled={isPosting}
            onDrop={(acceptedFiles) =>
              setImages((prev) => [...prev, ...acceptedFiles].slice(0, 2))
            }
          >
            {({ getRootProps, getInputProps }) => (
              <Box
                {...getRootProps()}
                border={`2px dashed ${palette.primary.main}`}
                p="1.25rem"
                width="100%"
                mt="1.25rem"
                sx={{ "&:hover": { cursor: isPosting ? "default" : "pointer" } }}
              >
                <input {...getInputProps()} />
                <Collapse
                  in={images.length === 0}
                  timeout={0}
                  unmountOnExit
                >
                  <Typography>Kaleidoskope hier hochladen</Typography>
                </Collapse>
                <Collapse
                  in={images.length > 0}
                  timeout={{ enter: 300, exit: 0 }}
                  unmountOnExit
                >
                  <Box
                    display="grid"
                    gridTemplateColumns={isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, 1fr)"}
                    gap="0.75rem"
                  >
                    {previewImages.map((image, index) => (
                      <Box key={image.id} sx={{ minWidth: 0 }}>
                        <img
                          src={image.url}
                          alt="preview"
                          style={{
                            width: "100%",
                            height: "140px",
                            objectFit: "contain",
                            borderRadius: "8px",
                            backgroundColor: palette.neutral.light,
                            display: "block",
                          }}
                        />
                        <FlexBetween mt="0.65rem" sx={{ minWidth: 0 }}>
                          <Typography
                            fontSize="0.75rem"
                            noWrap
                            sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {image.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setImages((prev) => prev.filter((_, i) => i !== index));
                            }}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </FlexBetween>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Dropzone>
          <Box mt="0.75rem" display="flex" alignItems="center" gap={0.75}>
            <Typography color={mediumMain}>
              {images.length}/2 Bilder ausgewählt
            </Typography>
            <Tooltip
              title="Bitte lade die Vorderseite und die Rückseite hoch."
              slotProps={{ tooltip: { sx: INFO_TOOLTIP_SX } }}
            >
              <IconButton size="small" sx={{ p: 0.25 }}>
                <HelpOutline sx={{ fontSize: "1rem", color: mediumMain }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Collapse>

      {/* Login Modal */}
      <Modal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        disableScrollLock
      >
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="flex-end" mb={0.25}>
            <IconButton onClick={() => setLoginModalOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Typography
            variant="h6"
            align="center"
            sx={{ px: { xs: 1, sm: 2 }, lineHeight: 1.45, mb: 2 }}
          >
              Du musst eingeloggt sein, um Kaleidoskope hochzuladen.
          </Typography>
          <Box display="flex" justifyContent="center" pb={0.5}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/einloggen");
              }}
            >
              Einloggen
            </Button>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarState.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbarState((prev) => ({
            ...prev,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() =>
            setSnackbarState((prev) => ({
              ...prev,
              open: false,
            }))
          }
          severity={snackbarState.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
      </WidgetWrapper>
    </ClickAwayListener>
  );
};

export default MyPostWidget;
